/**
 * RequireSuperAdmin — restricts a route to the SUPER_ADMIN role.
 *
 * Distinct from RequirePermission, which gates on the permission SET derived
 * from a role. Several surfaces (yield arbitrage, title lookup, the Leads
 * suite, admin user management) carry commercial or personal data that other
 * admin roles should not see at all, even where their permission set would
 * otherwise allow the underlying view.
 *
 * ⚠️ This hides the UI. It is NOT the access control. Every endpoint behind
 * these pages is separately gated server-side, because anything enforced only
 * in the browser is bypassed by calling the API directly.
 */
import type { ReactNode } from "react";

import { useAuthStore } from "@/store/authStore";
import { useViewModeStore } from "@/store/viewModeStore";
import { useBrandingStore } from "@/store/brandingStore";

export function useIsSuperAdmin(): boolean {
  const role = useAuthStore((s) => s.user?.role ?? "");
  return role.trim().toLowerCase() === "super_admin";
}

/**
 * DATA_ADMIN or SUPER_ADMIN, and deliberately NOT analyst (MPPI-00711).
 *
 * Mirrors the backend's `require_admin_roles(AdminRole.DATA_ADMIN)`, which is
 * exact membership plus SUPER_ADMIN rather than the `ROLE_ACCESS` ladder — the
 * ladder lets an analyst satisfy a DATA_ADMIN gate, and these screens are not
 * for analysts. The menu is a convenience; the API guard is the access
 * control, so if these two ever disagree the API is right.
 */
export function useIsDataAdmin(): boolean {
  const role = useAuthStore((s) => s.user?.role ?? "");
  const r = role.trim().toLowerCase();
  return r === "data_admin" || r === "super_admin";
}

/**
 * The platform owner's account.
 *
 * Deliberately an identity, not a role: the operator asked for several menu
 * sections to be visible to one named person, and roles are assigned by
 * whoever manages users. An email is checkable at a glance and cannot be
 * granted by mistake.
 */
// Fallback only — used when a brand has not named its own owner.
const OWNER_EMAIL = "admin@mppi.ae";

/**
 * True only for the owner account.
 *
 * ⚠️ Menu visibility only. The endpoints behind these sections are gated
 * server-side by role, and that is what actually stops access — a hidden link
 * is a tidier menu, not a permission.
 */
export function useIsOwner(): boolean {
  const email = useAuthStore((s) => s.user?.email ?? "");
  // Per BRAND, not global (MPPI-00275). The constant below was right when
  // there was one tenant; with a second, HouseKey's own super-admin is not
  // admin@mppi.ae and the owner sections were invisible to the person who
  // owns that brand. This keeps the property the constant was chosen for —
  // an identity, not a role — and makes the identity per-tenant.
  const brandOwner = useBrandingStore((s) => s.branding.owner_email);
  const owner = (brandOwner || OWNER_EMAIL).trim().toLowerCase();
  return email.trim().toLowerCase() === owner;
}

/**
 * True when the owner is looking at the owner's own view.
 *
 * Use this — not `useIsOwner` — to gate owner-only *content*, so the
 * Super/User View toggle actually changes what renders. `useIsOwner` stays
 * the identity check and is what decides whether the toggle itself appears:
 * the control has to survive being switched off, or User View would be a
 * one-way door out of the owner's own dashboard.
 */
export function useIsOwnerView(): boolean {
  const isOwner = useIsOwner();
  const mode = useViewModeStore((s) => s.mode);
  return isOwner && mode === "super";
}

/**
 * DATA_ADMIN or SUPER_ADMIN (MPPI-00711).
 *
 * Mirrors `require_admin_roles(AdminRole.DATA_ADMIN)` on the API. It exists
 * because the sidebar and the ROUTE are two separate gates and MPPI-00711
 * changed only the first: six menu entries were opened to data admins while
 * their routes still rendered "This page is limited to Super Admin users", so
 * the widening produced a menu of dead ends. The menu is a convenience; this
 * is what decides whether the page renders, and the API decides the data.
 */
export function RequireDataAdmin({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const isDataAdmin = useIsDataAdmin();

  if (!isDataAdmin) {
    return (
      <>
        {fallback || (
          <div className="flex flex-col items-center justify-center p-12">
            <h2 className="text-lg font-semibold text-gray-900">Restricted</h2>
            <p className="mt-1 text-sm text-gray-500">
              This page is limited to Data Admin and Super Admin users.
            </p>
          </div>
        )}
      </>
    );
  }
  return <>{children}</>;
}

export function RequireSuperAdmin({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const isSuperAdmin = useIsSuperAdmin();

  if (!isSuperAdmin) {
    return (
      <>
        {fallback || (
          <div className="flex flex-col items-center justify-center p-12">
            <h2 className="text-lg font-semibold text-gray-900">Restricted</h2>
            <p className="mt-1 text-sm text-gray-500">
              This page is limited to Super Admin users.
            </p>
          </div>
        )}
      </>
    );
  }
  return <>{children}</>;
}
